import React, { useState, useRef, useEffect } from "react";
import { useToast } from "../../../../contexts/ToastContext";
import { getApiErrorMessage } from "../../../../utils/errorUtils";

interface InlineEditCellProps {
  value: string | number;
  type: "text" | "number" | "time" | "textarea";
  onSave: (value: string) => Promise<void> | void;
  className?: string;
  placeholder?: string;
  suffix?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  textareaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export const InlineEditCell: React.FC<InlineEditCellProps> = ({
  value,
  type,
  onSave,
  className = "",
  placeholder = "",
  suffix = "",
  inputProps = {},
  textareaProps = {},
}) => {
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(String(value));
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      if (type === "textarea" && textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      } else if (inputRef.current) {
        inputRef.current.focus();
        if (type === "text") {
          inputRef.current.select();
        }
      }
    }
  }, [isEditing, type]);

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (editValue === String(value)) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Save failed:", error);
      addToast({
        type: "error",
        message: getApiErrorMessage(error, "Nie udało się zapisać zmian"),
      });
      setEditValue(String(value)); // Reset to original value
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(String(value));
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && type !== "textarea") {
      e.preventDefault();
      handleSave();
    } else if (
      e.key === "Enter" &&
      type === "textarea" &&
      (e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const displayValue = () => {
    if (type === "number" && suffix) {
      return `${value} ${suffix}`;
    }
    if (type === "time") {
      if (!value) {
        return "🕒"; // Ikona zegarka dla pustych pól czasu
      }
      // Formatowanie czasu do HH:MM jeśli jest w formacie H:MM lub zawiera sekundy
      const timeStr = String(value);
      if (timeStr.includes(":")) {
        const [hours, minutes] = timeStr.split(":");
        const formattedHours = hours.padStart(2, "0");
        const formattedMinutes = minutes ? minutes.padStart(2, "0") : "00";
        return `${formattedHours}:${formattedMinutes}`;
      }
      return timeStr;
    }
    return String(value || placeholder);
  };

  if (isEditing) {
    return (
      <div className="relative">
        {type === "textarea" ? (
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={`border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none ${className}`}
            placeholder={placeholder}
            disabled={isSaving}
            rows={3}
            {...textareaProps}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={`border border-blue-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
            placeholder={placeholder}
            disabled={isSaving}
            {...(type === "time" ? { step: "60", ...inputProps } : inputProps)}
          />
        )}
        {isSaving && (
          <div className="absolute -top-1 -right-1">
            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleStartEdit}
      className={`cursor-pointer hover:bg-gray-100 rounded px-1 py-1 transition-colors ${className} ${
        !value && placeholder ? "text-gray-400" : ""
      } ${type === "time" && !value ? "border border-dashed border-gray-300 text-center" : ""}`}
      title={
        type === "time" ? "Kliknij aby ustawić godzinę" : "Kliknij aby edytować"
      }
    >
      {displayValue()}
    </div>
  );
};
